import React, { useEffect, useState } from 'react'
import styled from 'styled-components'

const PAGE_SIZE = 1000

/*
 * =========================================================
 * PAGE
 * =========================================================
 */

const Page = styled.div`
  max-width: 1750px;
  margin: 0 auto;
  padding: 32px 24px;
  color: #222;
`

const SearchPanel = styled.div`
  margin-bottom: 28px;
`

const SearchLabel = styled.div`
  font-size: 18px;
  font-weight: 600;
  margin-bottom: 8px;
`

const SearchRow = styled.div`
  display: flex;
  gap: 10px;

  input {
    flex: 1;
    padding: 12px 14px;
    font-size: 16px;
    border: 1px solid #ccc;
    border-radius: 6px;
  }

  button {
    padding: 12px 24px;
    border: none;
    border-radius: 6px;
    background: #00552e;
    color: white;
    font-size: 16px;
    cursor: pointer;
  }

  button:hover {
    background: #003f22;
  }

  button:disabled {
    opacity: 0.6;
    cursor: default;
  }
`

const SearchHelp = styled.div`
  margin-top: 7px;
  font-size: 13px;
  color: #777;
`

const Message = styled.div`
  padding: 30px 0;
  color: #666;
`

const ErrorMessage = styled(Message)`
  color: #a40000;
`

const ResultsHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: end;
  gap: 20px;
  margin-bottom: 12px;

  h2 {
    margin: 0;
  }

  span {
    color: #777;
    font-size: 14px;
  }
`

const TableWrap = styled.div`
  border: 1px solid #ddd;
  border-radius: 8px;
  overflow-x: auto;
`

const SortableHeader = styled.th`
  cursor: pointer;
  user-select: none;
  white-space: nowrap;

  &:hover {
    background: #e8e8e8 !important;
  }
`

const SortIndicator = styled.span`
  margin-left: 6px;
  font-size: 11px;
  color: #777;
`

/*
 * =========================================================
 * EXPANDED TABLE
 * =========================================================
 */

const ResultsTable = styled.table`
  width: 100%;
  min-width: 1350px;
  border-collapse: collapse;

  th {
    text-align: left;
    padding: 13px 16px;
    background: #f4f4f4;
    color: #555;
    font-size: 14px;
    font-weight: 600;
    vertical-align: top;
  }

  td {
    padding: 14px 16px;
    border-top: 1px solid #eee;
    vertical-align: top;
  }

  tbody tr {
    cursor: pointer;
  }

  tbody tr:hover {
    background: #f7faf8;
  }

  .gene {
    font-weight: 600;
    color: #00552e;
  }

  .locus {
    font-family: monospace;
    white-space: nowrap;
  }
`

/*
 * =========================================================
 * GENOTYPE COMPOSITION
 * =========================================================
 */

const GenotypeComposition = styled.div`
  font-size: 13px;
  line-height: 1.6;
  min-width: 140px;
`

const GenotypeLine = styled.div`
  display: block;
  white-space: nowrap;

  strong {
    font-weight: 600;
  }
`

const CalledLine = styled.div`
  display: block;
  margin-top: 5px;
  padding-top: 5px;
  border-top: 1px solid #ddd;
  color: #666;
  white-space: nowrap;

  strong {
    color: #333;
  }
`

const EmptyValue = styled.div`
  color: #888;
`

/*
 * =========================================================
 * COLLAPSED TREE TABLE
 * =========================================================
 */

const CollapsedTable = styled.table`
  width: 100%;
  min-width: 1450px;
  border-collapse: collapse;

  th {
    text-align: left;
    padding: 13px 16px;
    background: #f4f4f4;
    color: #555;
    font-size: 14px;
    font-weight: 600;
    vertical-align: top;
  }

  td {
    padding: 12px 16px;
    border-top: 1px solid #eee;
    vertical-align: top;
  }

  th:nth-child(1) {
    width: 22%;
  }

  th:nth-child(2) {
    width: 20%;
  }

  th:nth-child(3) {
    width: 6%;
  }

  th:nth-child(4) {
    width: 11%;
  }

  th:nth-child(5) {
    width: 16%;
  }

  th:nth-child(6) {
    width: 16%;
  }

  th:nth-child(7) {
    width: 9%;
  }
`

const GeneSummaryRow = styled.tr`
  cursor: pointer;
  background: #fff;

  &:hover {
    background: #f7faf8;
  }

  td {
    font-weight: 500;
  }
`

const LocusSummaryRow = styled.tr`
  background: #fafafa;
  cursor: pointer;

  &:hover {
    background: #f6f8f7;
  }
`

const MultiLocusRow = styled(LocusSummaryRow)``

const MotifRow = styled.tr`
  background: #fff;
  cursor: pointer;

  td {
    border-top: 1px solid #f1f1f1;
  }

  &:hover {
    background: #f7faf8;
  }
`

const TreeLabel = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 8px;
`

const GeneLabel = styled(TreeLabel)`
  color: #00552e;
  font-weight: 600;
`

const LocusLabel = styled(TreeLabel)`
  padding-left: 28px;
`

const MotifOnlyLabel = styled.div`
  padding-left: 18px;
  font-family: monospace;
  font-weight: 600;
  color: #333;
`

const ExpandButton = styled.button`
  width: 18px;
  min-width: 18px;
  height: 20px;
  padding: 0;
  border: 0;
  background: transparent;
  color: #555;
  cursor: pointer;
  font-size: 12px;
  line-height: 20px;
`

const SpacerIcon = styled.span`
  display: inline-block;
  width: 18px;
  min-width: 18px;
`

const LocusText = styled.span`
  font-family: monospace;
  white-space: nowrap;
`

const MotifText = styled.span`
  font-family: monospace;
`

const SummaryText = styled.span`
  color: #666;
`

const CountBadge = styled.span`
  display: inline-block;
  padding: 4px 9px;
  border-radius: 12px;
  background: #eee;
  color: #555;
  font-size: 12px;
  white-space: nowrap;
`

/*
 * =========================================================
 * VIEW TOGGLE
 * =========================================================
 */

const ViewToggle = styled.div`
  display: flex;
  margin-bottom: 14px;

  button {
    border: 1px solid #ccc;
    background: white;
    padding: 7px 13px;
    cursor: pointer;
    font-size: 13px;
  }

  button:first-child {
    border-radius: 6px 0 0 6px;
  }

  button:last-child {
    border-radius: 0 6px 6px 0;
    border-left: none;
  }

  button:hover {
    background: #f4f4f4;
  }

  button.active {
    background: #00552e;
    border-color: #00552e;
    color: white;
  }
`

const ClearSearchButton = styled.button`
  border: 1px solid #ccc;
  background: white;
  border-radius: 6px;
  padding: 7px 12px;
  cursor: pointer;
  margin-left: 12px;

  &:hover {
    background: #f4f4f4;
  }
`

/*
 * =========================================================
 * DETAIL PAGE
 * =========================================================
 */

const BackButton = styled.button`
  background: none;
  border: none;
  padding: 0;
  margin-bottom: 18px;
  color: #00552e;
  font-size: 15px;
  cursor: pointer;

  &:hover {
    text-decoration: underline;
  }
`

const LocusCard = styled.div`
  border: 1px solid #ddd;
  border-radius: 10px;
  padding: 24px;
  margin-bottom: 30px;
  background: #fff;
`

const LocusHeader = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 24px;
  align-items: flex-start;
  flex-wrap: wrap;

  h2 {
    margin: 0 0 8px;
    font-size: 28px;
  }
`

const Coordinate = styled.div`
  color: #666;
  font-family: monospace;
  font-size: 16px;
`

const Tags = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
`

const Tag = styled.div`
  background: #f2f3f2;
  border: 1px solid #e1e3e2;
  padding: 8px 12px;
  border-radius: 7px;
`

const InfoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 24px;
  margin-top: 28px;

  @media (max-width: 900px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 550px) {
    grid-template-columns: 1fr;
  }
`

const InfoLabel = styled.div`
  color: #777;
  font-size: 13px;
  margin-bottom: 5px;
`

const InfoValue = styled.div`
  font-size: 16px;
  overflow-wrap: anywhere;
`

const Section = styled.div`
  margin: 34px 0;
`

const SectionHeading = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  gap: 18px;
  margin-bottom: 18px;
  flex-wrap: wrap;
`

const SectionTitle = styled.h3`
  margin: 0 0 5px;
  font-size: 21px;
`

const SectionSubtitle = styled.div`
  color: #777;
`

const TotalCalled = styled.div`
  color: #666;
  font-size: 14px;

  strong {
    color: #222;
    font-size: 18px;
    margin-left: 5px;
  }
`

const CohortGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;

  @media (max-width: 700px) {
    grid-template-columns: 1fr;
  }
`

const CohortCard = styled.div`
  background: #f7f8f7;
  border: 1px solid #ddd;
  border-radius: 10px;
  padding: 22px;
`

const CohortHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 18px;
  margin-bottom: 18px;

  strong {
    font-size: 30px;
  }
`

const BarTrack = styled.div`
  width: 100%;
  height: 10px;
  background: #e3e5e4;
  border-radius: 999px;
  overflow: hidden;
`

const BarFill = styled.div`
  height: 100%;
  background: #3f8f5a;
  border-radius: 999px;
`

const EmptyBarMessage = styled.div`
  color: #777;
  font-size: 13px;
`

const CountPercentToggle = styled.div`
  display: flex;
  border: 1px solid #ccc;
  border-radius: 7px;
  overflow: hidden;

  button {
    border: 0;
    background: white;
    padding: 8px 14px;
    cursor: pointer;
    color: #555;
  }

  button + button {
    border-left: 1px solid #ccc;
  }

  button.active {
    background: #00552e;
    color: white;
  }
`

const GenotypeTable = styled.table`
  width: 100%;
  min-width: 1100px;
  border-collapse: collapse;

  th {
    text-align: left;
    padding: 13px 16px;
    background: #f4f4f4;
    color: #555;
    font-size: 14px;
    font-weight: 600;
  }

  td {
    padding: 15px 16px;
    border-top: 1px solid #eee;
    vertical-align: middle;
  }

  th:nth-child(2),
  th:nth-child(3),
  td:nth-child(2),
  td:nth-child(3) {
    text-align: right;
    width: 100px;
  }

  th:nth-child(4),
  th:nth-child(5) {
    width: 230px;
  }
`

const GenotypeName = styled.div`
  font-weight: 600;
`

const GenotypeDescription = styled.div`
  margin-top: 3px;
  color: #777;
  font-size: 12px;
`

const Distribution = styled.div`
  min-width: 180px;
`

const DistributionValue = styled.div`
  margin-top: 5px;
  color: #666;
  font-size: 12px;
`

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 12px;

  @media (max-width: 900px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 550px) {
    grid-template-columns: 1fr;
  }
`

const Stat = styled.div`
  background: #f7f8f7;
  border: 1px solid #ddd;
  padding: 18px;
  border-radius: 9px;
`

const StatLabel = styled.div`
  font-size: 13px;
  color: #777;
`

const StatValue = styled.div`
  font-size: 24px;
  font-weight: 700;
  margin-top: 5px;
`

/*
 * =========================================================
 * HELPERS
 * =========================================================
 */

const formatNumber = value => {
  if (value === null || value === undefined) {
    return '—'
  }

  return Number(value).toLocaleString()
}

const formatDecimal = value => {
  if (value === null || value === undefined) {
    return '—'
  }

  return Number(value).toFixed(2)
}

const calculatePercent = (count, total) => {
  const numerator = Number(count || 0)
  const denominator = Number(total || 0)

  if (denominator <= 0) {
    return 0
  }

  return (numerator / denominator) * 100
}

const formatPercent = (count, total) => {
  if (Number(total || 0) <= 0) {
    return '—'
  }

  return `${calculatePercent(count, total).toFixed(1)}%`
}

const renderGenotypeComposition = (
  homRef,
  hetRefAlt,
  hetAltAlt,
  homAlt,
  total
) => (
  <GenotypeComposition>
    <GenotypeLine>
      Hom-ref: <strong>{formatNumber(homRef)}</strong>
    </GenotypeLine>

    <GenotypeLine>
      Ref/Alt: <strong>{formatNumber(hetRefAlt)}</strong>
    </GenotypeLine>

    <GenotypeLine>
      Alt/Alt het: <strong>{formatNumber(hetAltAlt)}</strong>
    </GenotypeLine>

    <GenotypeLine>
      Hom-alt: <strong>{formatNumber(homAlt)}</strong>
    </GenotypeLine>

    <CalledLine>
      Called: <strong>{formatNumber(total)}</strong>
    </CalledLine>
  </GenotypeComposition>
)

const renderCaseGenotype = row =>
  renderGenotypeComposition(
    row.CASE_HOM_REF_N,
    row.CASE_HET_REF_ALT_N,
    row.CASE_HET_ALT_ALT_N,
    row.CASE_HOM_ALT_N,
    row.CASE_CALLED_N
  )

const renderControlGenotype = row =>
  renderGenotypeComposition(
    row.CONTROL_HOM_REF_N,
    row.CONTROL_HET_REF_ALT_N,
    row.CONTROL_HET_ALT_ALT_N,
    row.CONTROL_HOM_ALT_N,
    row.CONTROL_CALLED_N
  )

const renderDash = () => <EmptyValue>-</EmptyValue>

/*
 * =========================================================
 * GRAPHQL
 * =========================================================
 */

const STR_FIELDS = `
  LOCUS
  CHROM
  POS
  END
  VARID
  REPID
  RU
  MOTIF_LENGTH
  REF
  REF_REPCN
  RL

  CASE_CALLED_N
  CONTROL_CALLED_N
  TOTAL_CALLED_N

  CASE_HOM_REF_N
  CONTROL_HOM_REF_N

  CASE_HET_REF_ALT_N
  CONTROL_HET_REF_ALT_N

  CASE_HET_ALT_ALT_N
  CONTROL_HET_ALT_ALT_N

  CASE_HOM_ALT_N
  CONTROL_HOM_ALT_N

  CASE_AVG_REPCN
  CONTROL_AVG_REPCN

  CASE_MAX_REPCN
  CONTROL_MAX_REPCN
`

/*
 * =========================================================
 * GROUPING
 * =========================================================
 */

const getGeneKey = row =>
  row.VARID || row.REPID || 'Unknown'

const groupBrowseResults = rows => {
  const geneMap = new Map()

  rows.forEach(row => {
    const geneKey = getGeneKey(row)

    if (!geneMap.has(geneKey)) {
      geneMap.set(geneKey, {
        gene: geneKey,
        rows: [],
        lociMap: new Map(),
      })
    }

    const geneGroup = geneMap.get(geneKey)

    geneGroup.rows.push(row)

    const locusKey =
      row.LOCUS || 'Unknown locus'

    if (!geneGroup.lociMap.has(locusKey)) {
      geneGroup.lociMap.set(locusKey, {
        locus: locusKey,
        rows: [],
      })
    }

    geneGroup.lociMap
      .get(locusKey)
      .rows.push(row)
  })

  return Array.from(geneMap.values()).map(geneGroup => {
    const loci = Array.from(
      geneGroup.lociMap.values()
    ).map(locusGroup => {
      const refs = Array.from(
        new Set(
          locusGroup.rows
            .map(row => row.REF)
            .filter(Boolean)
        )
      )

      const motifs = Array.from(
        new Set(
          locusGroup.rows
            .map(row => row.RU)
            .filter(Boolean)
        )
      )

      return {
        locus: locusGroup.locus,
        rows: locusGroup.rows,
        refs,
        motifs,
        motifCount: locusGroup.rows.length,
      }
    })

    return {
      gene: geneGroup.gene,
      rows: geneGroup.rows,
      loci,
      locusCount: loci.length,
      motifCount: geneGroup.rows.length,
    }
  })
}

/*
 * =========================================================
 * COMPONENT
 * =========================================================
 */

const IPNTandemRepeats = () => {
  const [query, setQuery] = useState('')
  const [results, setResults] = useState([])

  const [browseResults, setBrowseResults] = useState([])
  const [browseTotal, setBrowseTotal] = useState(0)

  const [selected, setSelected] = useState(null)

  const [loading, setLoading] = useState(false)
  const [loadingBrowse, setLoadingBrowse] = useState(false)

  const [searched, setSearched] = useState(false)

  const [error, setError] = useState('')
  const [browseError, setBrowseError] = useState('')

  const [tableView, setTableView] =
    useState('collapsed')

  const [sortConfig, setSortConfig] = useState({
    field: null,
    direction: null,
  })

  const [genotypeDisplay, setGenotypeDisplay] =
    useState('count')

  const [expandedGenes, setExpandedGenes] =
    useState({})

  const [expandedLoci, setExpandedLoci] =
    useState({})

  /*
   * =========================================================
   * LOAD BROWSE DATA
   * =========================================================
   */

  const loadBrowseData = async () => {
    setLoadingBrowse(true)
    setBrowseError('')

    try {
      const graphqlQuery = `
        query {
          browseSTRLoci(
            page: 1
            pageSize: ${PAGE_SIZE}
          ) {
            total

            loci {
              ${STR_FIELDS}
            }
          }
        }
      `

      const response = await fetch('/api/str_ipn', {
        method: 'POST',

        headers: {
          'Content-Type': 'application/json',
        },

        body: JSON.stringify({
          query: graphqlQuery,
        }),
      })

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`)
      }

      const data = await response.json()

      if (data.errors && data.errors.length > 0) {
        throw new Error(
          data.errors
            .map(graphqlError => graphqlError.message)
            .join(', ')
        )
      }

      if (!data.data || !data.data.browseSTRLoci) {
        throw new Error('No browse data returned')
      }

      setBrowseResults(
        data.data.browseSTRLoci.loci || []
      )

      setBrowseTotal(
        data.data.browseSTRLoci.total || 0
      )
    } catch (err) {
      console.error('STR browse error:', err)

      setBrowseError(
        `Unable to load STR loci${
          err && err.message
            ? `: ${err.message}`
            : '.'
        }`
      )

      setBrowseResults([])
    } finally {
      setLoadingBrowse(false)
    }
  }

  useEffect(() => {
    loadBrowseData()
  }, [])

  /*
   * =========================================================
   * SEARCH
   * =========================================================
   */

  const searchSTR = async () => {
    const q = query.trim()

    if (!q) {
      setSearched(false)
      setResults([])
      setSelected(null)
      setError('')
      setExpandedGenes({})
      setExpandedLoci({})
      setTableView('collapsed')
      return
    }

    setLoading(true)
    setError('')
    setSelected(null)
    setResults([])
    setExpandedGenes({})
    setExpandedLoci({})

    try {
      const graphqlQuery = `
        query {
          strSearch(
            query: ${JSON.stringify(q)}
          ) {
            ${STR_FIELDS}
          }
        }
      `

      const response = await fetch('/api/str_ipn', {
        method: 'POST',

        headers: {
          'Content-Type': 'application/json',
        },

        body: JSON.stringify({
          query: graphqlQuery,
        }),
      })

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`)
      }

      const data = await response.json()

      if (data.errors && data.errors.length > 0) {
        throw new Error(
          data.errors
            .map(graphqlError => graphqlError.message)
            .join(', ')
        )
      }

      setResults(
        data.data && data.data.strSearch
          ? data.data.strSearch
          : []
      )

      setTableView('expanded')
      setSearched(true)
    } catch (err) {
      console.error('STR search error:', err)

      setError(
        `Unable to search STR data${
          err && err.message
            ? `: ${err.message}`
            : '.'
        }`
      )

      setResults([])
      setSearched(true)
    } finally {
      setLoading(false)
    }
  }

  const handleKeyDown = event => {
    if (event.key === 'Enter') {
      searchSTR()
    }
  }

  const clearSearch = () => {
    setQuery('')
    setResults([])
    setSearched(false)
    setSelected(null)
    setError('')
    setExpandedGenes({})
    setExpandedLoci({})
    setTableView('collapsed')
  }

  const selectLocus = row => {
    setSelected(row)
    setGenotypeDisplay('count')
  }

  /*
   * =========================================================
   * TREE EXPANSION
   * =========================================================
   */

  const toggleGene = gene => {
    setExpandedGenes(previous => ({
      ...previous,
      [gene]: !previous[gene],
    }))
  }

  const getLocusKey = (gene, locus) =>
    `${gene}::${locus}`

  const toggleLocus = (gene, locus) => {
    const key = getLocusKey(gene, locus)

    setExpandedLoci(previous => ({
      ...previous,
      [key]: !previous[key],
    }))
  }

  /*
   * =========================================================
   * SORTING
   * =========================================================
   */

  const handleSort = field => {
    setSortConfig(previous => {
      if (previous.field !== field) {
        return {
          field,
          direction: 'asc',
        }
      }

      if (previous.direction === 'asc') {
        return {
          field,
          direction: 'desc',
        }
      }

      return {
        field: null,
        direction: null,
      }
    })
  }

  const getSortIndicator = field => {
    if (
      sortConfig.field !== field ||
      !sortConfig.direction
    ) {
      return '↕'
    }

    return sortConfig.direction === 'asc'
      ? '▲'
      : '▼'
  }

  const chromosomeOrder = chrom => {
    const value = String(chrom || '')
      .replace(/^chr/i, '')
      .toUpperCase()

    if (/^\d+$/.test(value)) {
      return Number(value)
    }

    if (value === 'X') return 23
    if (value === 'Y') return 24
    if (value === 'M' || value === 'MT') return 25

    return 100
  }

  const sortSTRRows = rows => {
    if (
      !sortConfig.field ||
      !sortConfig.direction
    ) {
      return rows
    }

    const sorted = [...rows]

    sorted.sort((a, b) => {
      let comparison = 0

      if (sortConfig.field === 'gene') {
        comparison = String(
          a.VARID || a.REPID || ''
        ).localeCompare(
          String(b.VARID || b.REPID || ''),
          undefined,
          {
            numeric: true,
            sensitivity: 'base',
          }
        )
      }

      if (
        sortConfig.field === 'locus' ||
        (
          sortConfig.field === 'gene' &&
          comparison === 0
        )
      ) {
        comparison =
          chromosomeOrder(a.CHROM) -
          chromosomeOrder(b.CHROM)

        if (comparison === 0) {
          comparison =
            Number(a.POS || 0) -
            Number(b.POS || 0)
        }

        if (comparison === 0) {
          comparison =
            Number(a.END || 0) -
            Number(b.END || 0)
        }
      }

      if (comparison === 0) {
        comparison = String(a.RU || '')
          .localeCompare(
            String(b.RU || ''),
            undefined,
            { sensitivity: 'base' }
          )
      }

      return sortConfig.direction === 'desc'
        ? -comparison
        : comparison
    })

    return sorted
  }

  /*
   * =========================================================
   * CURRENT DATA
   * =========================================================
   */

  const rawDisplayedLoci =
    searched ? results : browseResults

  const displayedLoci =
    sortSTRRows(rawDisplayedLoci)

  const groupedLoci =
    groupBrowseResults(displayedLoci)

  /*
   * =========================================================
   * EXPANDED TABLE
   * =========================================================
   */

  const renderExpandedTable = () => (
    <TableWrap>
      <ResultsTable>
        <thead>
          <tr>
            <SortableHeader
              onClick={() => handleSort('gene')}
            >
              GENE / VARID
              <SortIndicator>
                {getSortIndicator('gene')}
              </SortIndicator>
            </SortableHeader>

            <SortableHeader
              onClick={() => handleSort('locus')}
            >
              LOCUS
              <SortIndicator>
                {getSortIndicator('locus')}
              </SortIndicator>
            </SortableHeader>

            <th>REF</th>
            <th>MOTIF</th>
            <th>CASE GENOTYPES</th>
            <th>CONTROL GENOTYPES</th>
          </tr>
        </thead>

        <tbody>
          {displayedLoci.map((row, index) => (
            <tr
              key={`${row.LOCUS}-${row.RU}-${index}`}
              onClick={() => selectLocus(row)}
            >
              <td className="gene">
                {row.VARID ||
                  row.REPID ||
                  '—'}
              </td>

              <td className="locus">
                {row.LOCUS || '—'}
              </td>

              <td>{row.REF || '—'}</td>

              <td>{row.RU || '—'}</td>

              <td>
                {renderCaseGenotype(row)}
              </td>

              <td>
                {renderControlGenotype(row)}
              </td>
            </tr>
          ))}
        </tbody>
      </ResultsTable>
    </TableWrap>
  )

  /*
   * =========================================================
   * COLLAPSED TABLE
   * =========================================================
   */

  const renderCollapsedTable = () => (
    <TableWrap>
      <CollapsedTable>
        <thead>
          <tr>
            <SortableHeader
              onClick={() => handleSort('gene')}
            >
              GENE / VARID
              <SortIndicator>
                {getSortIndicator('gene')}
              </SortIndicator>
            </SortableHeader>

            <SortableHeader
              onClick={() => handleSort('locus')}
            >
              LOCUS
              <SortIndicator>
                {getSortIndicator('locus')}
              </SortIndicator>
            </SortableHeader>

            <th>REF</th>
            <th>MOTIF</th>
            <th>CASE GENOTYPES</th>
            <th>CONTROL GENOTYPES</th>
            <th>MOTIF COUNT</th>
          </tr>
        </thead>

        <tbody>
          {groupedLoci.map(geneGroup => {
            const geneOpen =
              !!expandedGenes[geneGroup.gene]

            return (
              <React.Fragment
                key={geneGroup.gene}
              >
                <GeneSummaryRow
                  onClick={() =>
                    toggleGene(geneGroup.gene)
                  }
                >
                  <td>
                    <GeneLabel>
                      <ExpandButton
                        type="button"
                        tabIndex="-1"
                        onClick={event => {
                          event.stopPropagation()

                          toggleGene(
                            geneGroup.gene
                          )
                        }}
                      >
                        {geneOpen
                          ? '▼'
                          : '▶'}
                      </ExpandButton>

                      <span>
                        {geneGroup.gene}
                      </span>
                    </GeneLabel>
                  </td>

                  <td>
                    <SummaryText>
                      {geneGroup.locusCount.toLocaleString()}{' '}
                      locus
                      {geneGroup.locusCount !== 1
                        ? 'es'
                        : ''}
                    </SummaryText>
                  </td>

                  <td>{renderDash()}</td>
                  <td>{renderDash()}</td>
                  <td>{renderDash()}</td>
                  <td>{renderDash()}</td>

                  <td>
                    <CountBadge>
                      {geneGroup.motifCount.toLocaleString()}{' '}
                      motif
                      {geneGroup.motifCount !== 1
                        ? 's'
                        : ''}
                    </CountBadge>
                  </td>
                </GeneSummaryRow>

                {geneOpen &&
                  geneGroup.loci.map(
                    locusGroup => {
                      const multipleMotifs =
                        locusGroup.motifCount > 1

                      const key =
                        getLocusKey(
                          geneGroup.gene,
                          locusGroup.locus
                        )

                      const locusOpen =
                        !!expandedLoci[key]

                      const singleRow =
                        !multipleMotifs
                          ? locusGroup.rows[0]
                          : null

                      const LocusRow =
                        multipleMotifs
                          ? MultiLocusRow
                          : LocusSummaryRow

                      return (
                        <React.Fragment key={key}>
                          <LocusRow
                            onClick={() => {
                              if (multipleMotifs) {
                                toggleLocus(
                                  geneGroup.gene,
                                  locusGroup.locus
                                )
                              } else if (
                                singleRow
                              ) {
                                selectLocus(
                                  singleRow
                                )
                              }
                            }}
                          >
                            <td>
                              <LocusLabel>
                                {multipleMotifs ? (
                                  <ExpandButton
                                    type="button"
                                    tabIndex="-1"
                                    onClick={event => {
                                      event.stopPropagation()

                                      toggleLocus(
                                        geneGroup.gene,
                                        locusGroup.locus
                                      )
                                    }}
                                  >
                                    {locusOpen
                                      ? '▼'
                                      : '▶'}
                                  </ExpandButton>
                                ) : (
                                  <SpacerIcon />
                                )}

                                <span>
                                  Locus
                                </span>
                              </LocusLabel>
                            </td>

                            <td>
                              <LocusText>
                                {
                                  locusGroup.locus
                                }
                              </LocusText>
                            </td>

                            <td>
                              {locusGroup.refs.length
                                ? locusGroup.refs.join(
                                    '; '
                                  )
                                : '—'}
                            </td>

                            <td>
                              <MotifText>
                                {locusGroup
                                  .motifs.length
                                  ? locusGroup.motifs.join(
                                      '; '
                                    )
                                  : '—'}
                              </MotifText>
                            </td>

                            <td>
                              {singleRow
                                ? renderCaseGenotype(
                                    singleRow
                                  )
                                : renderDash()}
                            </td>

                            <td>
                              {singleRow
                                ? renderControlGenotype(
                                    singleRow
                                  )
                                : renderDash()}
                            </td>

                            <td>
                              <CountBadge>
                                {locusGroup.motifCount.toLocaleString()}{' '}
                                motif
                                {locusGroup.motifCount !==
                                1
                                  ? 's'
                                  : ''}
                              </CountBadge>
                            </td>
                          </LocusRow>

                          {multipleMotifs &&
                            locusOpen &&
                            locusGroup.rows.map(
                              (row, index) => (
                                <MotifRow
                                  key={`${key}-${row.RU}-${index}`}
                                  onClick={() =>
                                    selectLocus(
                                      row
                                    )
                                  }
                                >
                                  <td />
                                  <td />
                                  <td />

                                  <td>
                                    <MotifOnlyLabel>
                                      {row.RU ||
                                        '—'}
                                    </MotifOnlyLabel>
                                  </td>

                                  <td>
                                    {renderCaseGenotype(
                                      row
                                    )}
                                  </td>

                                  <td>
                                    {renderControlGenotype(
                                      row
                                    )}
                                  </td>

                                  <td />
                                </MotifRow>
                              )
                            )}
                        </React.Fragment>
                      )
                    }
                  )}
              </React.Fragment>
            )
          })}
        </tbody>
      </CollapsedTable>
    </TableWrap>
  )

  /*
   * =========================================================
   * MAIN
   * =========================================================
   */

  return (
    <Page>
      <SearchPanel>
        <SearchLabel>
          Search STR locus
        </SearchLabel>

        <SearchRow>
          <input
            value={query}
            onChange={event =>
              setQuery(event.target.value)
            }
            onKeyDown={handleKeyDown}
            placeholder="Gene, VARID, motif, locus, or gene/locus + motif"
          />

          <button
            type="button"
            onClick={searchSTR}
            disabled={loading}
          >
            {loading
              ? 'Searching...'
              : 'Search'}
          </button>
        </SearchRow>

        <SearchHelp>
          Examples: TNFRSF1B; AAGGAG;
          1:12173146-12174973;
          TNFRSF1B,AAGGAG
        </SearchHelp>
      </SearchPanel>

      {loading && (
        <Message>
          Searching...
        </Message>
      )}

      {!searched &&
        loadingBrowse &&
        !selected && (
          <Message>
            Loading STR loci...
          </Message>
        )}

      {!loading && error && (
        <ErrorMessage>
          {error}
        </ErrorMessage>
      )}

      {!searched &&
        !loadingBrowse &&
        browseError && (
          <ErrorMessage>
            {browseError}
          </ErrorMessage>
        )}

      {!loading &&
        !error &&
        !selected &&
        searched &&
        results.length === 0 && (
          <Message>
            No STR loci found for "{query}".

            <ClearSearchButton
              type="button"
              onClick={clearSearch}
            >
              Show all loci
            </ClearSearchButton>
          </Message>
        )}

      {!selected &&
        !loading &&
        !loadingBrowse &&
        !error &&
        (!browseError || searched) &&
        displayedLoci.length > 0 && (
          <>
            <ResultsHeader>
              <h2>
                {searched
                  ? 'Search results'
                  : 'All STR loci'}
              </h2>

              <span>
                {searched
                  ? `${displayedLoci.length.toLocaleString()} result${
                      displayedLoci.length !==
                      1
                        ? 's'
                        : ''
                    }`
                  : `${browseTotal.toLocaleString()} motifs across ${groupedLoci.length.toLocaleString()} Gene / VARID groups`}
              </span>
            </ResultsHeader>

            {searched && (
              <div
                style={{
                  marginBottom: '12px',
                }}
              >
                Showing matches for{' '}
                <strong>
                  {query}
                </strong>

                <ClearSearchButton
                  type="button"
                  onClick={clearSearch}
                >
                  Show all loci
                </ClearSearchButton>
              </div>
            )}

            <ViewToggle>
              <button
                type="button"
                className={
                  tableView ===
                  'collapsed'
                    ? 'active'
                    : ''
                }
                onClick={() =>
                  setTableView(
                    'collapsed'
                  )
                }
              >
                Collapsed
              </button>

              <button
                type="button"
                className={
                  tableView ===
                  'expanded'
                    ? 'active'
                    : ''
                }
                onClick={() =>
                  setTableView(
                    'expanded'
                  )
                }
              >
                Expanded
              </button>
            </ViewToggle>

            {tableView === 'expanded'
              ? renderExpandedTable()
              : renderCollapsedTable()}
          </>
        )}

      {/*
       * =====================================================
       * INDIVIDUAL MOTIF DETAIL
       * =====================================================
       */}

      {selected && (
        <>
          <BackButton
            type="button"
            onClick={() =>
              setSelected(null)
            }
          >
            ← Back to{' '}
            {searched
              ? 'search results'
              : 'all STR loci'}
          </BackButton>

          <LocusCard>
            <LocusHeader>
              <div>
                <h2>
                  {selected.VARID ||
                    selected.REPID ||
                    selected.LOCUS}
                </h2>

                <Coordinate>
                  {selected.CHROM}:
                  {formatNumber(
                    selected.POS
                  )}
                  –
                  {formatNumber(
                    selected.END
                  )}
                </Coordinate>
              </div>

              <Tags>
                <Tag>
                  Motif{' '}
                  <strong>
                    {selected.RU ||
                      '—'}
                  </strong>
                </Tag>

                <Tag>
                  {formatNumber(
                    selected.MOTIF_LENGTH
                  )}{' '}
                  bp motif
                </Tag>

                <Tag>
                  RL{' '}
                  {formatNumber(
                    selected.RL
                  )}{' '}
                  bp
                </Tag>
              </Tags>
            </LocusHeader>

            <InfoGrid>
              <div>
                <InfoLabel>
                  Locus
                </InfoLabel>

                <InfoValue>
                  {selected.LOCUS ||
                    '—'}
                </InfoValue>
              </div>

              <div>
                <InfoLabel>
                  REPID
                </InfoLabel>

                <InfoValue>
                  {selected.REPID ||
                    '—'}
                </InfoValue>
              </div>

              <div>
                <InfoLabel>
                  VARID
                </InfoLabel>

                <InfoValue>
                  {selected.VARID ||
                    '—'}
                </InfoValue>
              </div>

              <div>
                <InfoLabel>
                  Reference
                </InfoLabel>

                <InfoValue>
                  {selected.REF ||
                    '—'}
                </InfoValue>
              </div>

              <div>
                <InfoLabel>
                  Reference repeat
                  count
                </InfoLabel>

                <InfoValue>
                  {formatNumber(
                    selected.REF_REPCN
                  )}
                </InfoValue>
              </div>

              <div>
                <InfoLabel>
                  Repeat unit
                </InfoLabel>

                <InfoValue>
                  {selected.RU ||
                    '—'}
                </InfoValue>
              </div>

              <div>
                <InfoLabel>
                  Motif length
                </InfoLabel>

                <InfoValue>
                  {formatNumber(
                    selected.MOTIF_LENGTH
                  )}{' '}
                  bp
                </InfoValue>
              </div>

              <div>
                <InfoLabel>
                  Reference locus
                  length
                </InfoLabel>

                <InfoValue>
                  {formatNumber(
                    selected.RL
                  )}{' '}
                  bp
                </InfoValue>
              </div>
            </InfoGrid>
          </LocusCard>

          {/*
           * ===============================================
           * COHORT CALLS
           * ===============================================
           */}

          <Section>
            <SectionHeading>
              <div>
                <SectionTitle>
                  Cohort calls
                </SectionTitle>

                <SectionSubtitle>
                  Called genotypes at
                  this locus
                </SectionSubtitle>
              </div>

              <TotalCalled>
                Total called
                <strong>
                  {formatNumber(
                    Number(
                      selected.CASE_CALLED_N ||
                        0
                    ) +
                      Number(
                        selected.CONTROL_CALLED_N ||
                          0
                      )
                  )}
                </strong>
              </TotalCalled>
            </SectionHeading>

            <CohortGrid>
              <CohortCard>
                <CohortHeader>
                  <span>Cases</span>

                  <strong>
                    {formatNumber(
                      selected.CASE_CALLED_N
                    )}
                  </strong>
                </CohortHeader>

                {Number(
                  selected.CASE_CALLED_N ||
                    0
                ) > 0 ? (
                  <BarTrack>
                    <BarFill
                      style={{
                        width: `${calculatePercent(
                          selected.CASE_CALLED_N,
                          Number(
                            selected.CASE_CALLED_N ||
                              0
                          ) +
                            Number(
                              selected.CONTROL_CALLED_N ||
                                0
                            )
                        )}%`,
                      }}
                    />
                  </BarTrack>
                ) : (
                  <EmptyBarMessage>
                    No case calls
                    represented in this
                    aggregate row.
                  </EmptyBarMessage>
                )}
              </CohortCard>

              <CohortCard>
                <CohortHeader>
                  <span>
                    Controls
                  </span>

                  <strong>
                    {formatNumber(
                      selected.CONTROL_CALLED_N
                    )}
                  </strong>
                </CohortHeader>

                {Number(
                  selected.CONTROL_CALLED_N ||
                    0
                ) > 0 ? (
                  <BarTrack>
                    <BarFill
                      style={{
                        width: `${calculatePercent(
                          selected.CONTROL_CALLED_N,
                          Number(
                            selected.CASE_CALLED_N ||
                              0
                          ) +
                            Number(
                              selected.CONTROL_CALLED_N ||
                                0
                            )
                        )}%`,
                      }}
                    />
                  </BarTrack>
                ) : (
                  <EmptyBarMessage>
                    No control calls
                    represented in this
                    aggregate row.
                  </EmptyBarMessage>
                )}
              </CohortCard>
            </CohortGrid>
          </Section>

          {/*
           * ===============================================
           * GENOTYPE COMPOSITION
           * ===============================================
           */}

          <Section>
            <SectionHeading>
              <div>
                <SectionTitle>
                  Genotype composition
                </SectionTitle>

                <SectionSubtitle>
                  Counts classified
                  from GT
                </SectionSubtitle>
              </div>

              <CountPercentToggle>
                <button
                  type="button"
                  className={
                    genotypeDisplay ===
                    'count'
                      ? 'active'
                      : ''
                  }
                  onClick={() =>
                    setGenotypeDisplay(
                      'count'
                    )
                  }
                >
                  Count
                </button>

                <button
                  type="button"
                  className={
                    genotypeDisplay ===
                    'percent'
                      ? 'active'
                      : ''
                  }
                  onClick={() =>
                    setGenotypeDisplay(
                      'percent'
                    )
                  }
                >
                  Percent
                </button>
              </CountPercentToggle>
            </SectionHeading>

            <TableWrap>
              <GenotypeTable>
                <thead>
                  <tr>
                    <th>
                      Genotype class
                    </th>
                    <th>Cases</th>
                    <th>Controls</th>
                    <th>
                      Case distribution
                    </th>
                    <th>
                      Control
                      distribution
                    </th>
                  </tr>
                </thead>

                <tbody>
                  {[
                    {
                      name:
                        'Homozygous reference',
                      description:
                        '0/0',
                      caseCount:
                        selected.CASE_HOM_REF_N,
                      controlCount:
                        selected.CONTROL_HOM_REF_N,
                    },
                    {
                      name:
                        'Heterozygous ref/alt',
                      description:
                        'one reference + one alternate',
                      caseCount:
                        selected.CASE_HET_REF_ALT_N,
                      controlCount:
                        selected.CONTROL_HET_REF_ALT_N,
                    },
                    {
                      name:
                        'Heterozygous alt/alt',
                      description:
                        'two different alternate alleles',
                      caseCount:
                        selected.CASE_HET_ALT_ALT_N,
                      controlCount:
                        selected.CONTROL_HET_ALT_ALT_N,
                    },
                    {
                      name:
                        'Homozygous alternate',
                      description:
                        'same alternate allele twice',
                      caseCount:
                        selected.CASE_HOM_ALT_N,
                      controlCount:
                        selected.CONTROL_HOM_ALT_N,
                    },
                  ].map(genotype => {
                    const casePercent =
                      calculatePercent(
                        genotype.caseCount,
                        selected.CASE_CALLED_N
                      )

                    const controlPercent =
                      calculatePercent(
                        genotype.controlCount,
                        selected.CONTROL_CALLED_N
                      )

                    return (
                      <tr
                        key={
                          genotype.name
                        }
                      >
                        <td>
                          <GenotypeName>
                            {
                              genotype.name
                            }
                          </GenotypeName>

                          <GenotypeDescription>
                            {
                              genotype.description
                            }
                          </GenotypeDescription>
                        </td>

                        <td>
                          {genotypeDisplay ===
                          'count'
                            ? formatNumber(
                                genotype.caseCount
                              )
                            : formatPercent(
                                genotype.caseCount,
                                selected.CASE_CALLED_N
                              )}
                        </td>

                        <td>
                          {genotypeDisplay ===
                          'count'
                            ? formatNumber(
                                genotype.controlCount
                              )
                            : formatPercent(
                                genotype.controlCount,
                                selected.CONTROL_CALLED_N
                              )}
                        </td>

                        <td>
                          <Distribution>
                            <BarTrack>
                              <BarFill
                                style={{
                                  width: `${casePercent}%`,
                                }}
                              />
                            </BarTrack>

                            <DistributionValue>
                              {formatPercent(
                                genotype.caseCount,
                                selected.CASE_CALLED_N
                              )}
                            </DistributionValue>
                          </Distribution>
                        </td>

                        <td>
                          <Distribution>
                            <BarTrack>
                              <BarFill
                                style={{
                                  width: `${controlPercent}%`,
                                }}
                              />
                            </BarTrack>

                            <DistributionValue>
                              {formatPercent(
                                genotype.controlCount,
                                selected.CONTROL_CALLED_N
                              )}
                            </DistributionValue>
                          </Distribution>
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </GenotypeTable>
            </TableWrap>
          </Section>

          {/*
           * ===============================================
           * REPEAT LENGTH SUMMARY
           * ===============================================
           */}

          <Section>
            <SectionHeading>
              <div>
                <SectionTitle>
                  Repeat length summary
                </SectionTitle>

                <SectionSubtitle>
                  ExpansionHunter
                  repeat-count
                  statistics
                </SectionSubtitle>
              </div>
            </SectionHeading>

            <StatsGrid>
              <Stat>
                <StatLabel>
                  Case average REPCN
                </StatLabel>

                <StatValue>
                  {formatDecimal(
                    selected.CASE_AVG_REPCN
                  )}
                </StatValue>
              </Stat>

              <Stat>
                <StatLabel>
                  Case max REPCN
                </StatLabel>

                <StatValue>
                  {formatNumber(
                    selected.CASE_MAX_REPCN
                  )}
                </StatValue>
              </Stat>

              <Stat>
                <StatLabel>
                  Control average
                  REPCN
                </StatLabel>

                <StatValue>
                  {formatDecimal(
                    selected.CONTROL_AVG_REPCN
                  )}
                </StatValue>
              </Stat>

              <Stat>
                <StatLabel>
                  Control max REPCN
                </StatLabel>

                <StatValue>
                  {formatNumber(
                    selected.CONTROL_MAX_REPCN
                  )}
                </StatValue>
              </Stat>
            </StatsGrid>
          </Section>
        </>
      )}
    </Page>
  )
}

export default IPNTandemRepeats